import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Play, Pause, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useTheme } from '@emotion/react';
import clsx from 'clsx';
import DateRangeSelector from '../../components/DateRangeSelector';
import { CallTableSkeleton } from '../../components/SkeletonLoader';
import { Dialog } from '@mui/material';

const columns = [
  { id: 'Date', label: 'Date' },
  { id: 'Time', label: 'Time' },
  { id: 'Caller', label: 'Caller' },
  { id: 'CustomerSatisfaction', label: 'Customer Satisfaction' },
  { id: 'CallPurpose', label: 'Call Purpose' },
  { id: 'PreferredLanguage', label: 'Preferred Language' },
  { id: 'Sentiment', label: 'Sentiment' },
  { id: 'Summary', label: 'Summary' },
  { id: 'Transcript', label: 'Transcript' },
  { id: 'Recording', label: 'Recording' },
];

const CallAnalytics = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [analyticsData, setAnalyticsData] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [expandedCell, setExpandedCell] = useState({ row: null, column: null });
  const [modalContent, setModalContent] = useState("");
  const [prefetchedPages, setPrefetchedPages] = useState({});

  const handlePlayPause = (recordUrl) => {
    if (currentlyPlaying === recordUrl) {
      setCurrentlyPlaying(null);
      const audio = document.getElementById('audio-player');
      if (audio) {
        audio.pause();
      }
    } else {
      setCurrentlyPlaying(recordUrl);
      const audio = document.getElementById('audio-player');
      if (audio) {
        audio.src = recordUrl;
        audio.play();
      }
    }
  };

  const handleExpand = (rowIdx, columnId, content) => {
    setExpandedCell({ row: rowIdx, column: columnId });
    setModalContent(content);
  };
  const handleCloseModal = () => {
    setExpandedCell({ row: null, column: null });
    setModalContent("");
  };

  useEffect(() => {
    setOrganizationId(localStorage.getItem('userId'));
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    if (prefetchedPages[page]) {
      setAnalyticsData(prefetchedPages[page]);
      setLoading(false);
    } else {
      setLoading(true);
      prefetchPages(page);
    }
  }, [organizationId, dateRange, page]);

  const prefetchPages = async (pg) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/callanalytics/${organizationId}`);
      url.searchParams.append('startDate', dateRange.start);
      url.searchParams.append('endDate', dateRange.end);
      url.searchParams.append('page', pg.toString());
      url.searchParams.append('pageSize', pageSize.toString());
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPrefetchedPages(prev => ({ ...prev, ...data.pages }));
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
      setAnalyticsData(data.pages[pg] || []);
    } catch (error) {
      setError('Failed to load data. Please try again.');
      setAnalyticsData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async (retryCount = 0) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/callanalytics/${organizationId}`);
      url.searchParams.append('startDate', dateRange.start);
      url.searchParams.append('endDate', dateRange.end);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('pageSize', pageSize.toString());

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const mapAnalyticsRow = (row) => ({
        Date: row.Date,
        Time: row.Time,
        Caller: row.Caller,
        CustomerSatisfaction: row.CustomerSatisfaction,
        CallPurpose: row.CallPurpose,
        PreferredLanguage: row.PreferredLanguage,
        Sentiment: row.Sentiment,
        Summary: row.Summary,
        Transcript: row.Transcript,
        Recording: row.Recording,
      });
      setAnalyticsData(data.analytics.map(mapAnalyticsRow));
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
      setError(null);
    } catch (error) {
      if (retryCount < 2) {
        setTimeout(() => fetchAnalyticsData(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        setError('Failed to load data. Please try again.');
        setAnalyticsData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(1); // Reset to first page when date range changes
  };

  // Pagination rendering logic (copied and adapted from CallLogs.js)
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      if (prefetchedPages[newPage]) {
        setLoading(false); // Instantly show data for prefetched pages
        setAnalyticsData(prefetchedPages[newPage]);
        setPage(newPage);
      } else {
        setLoading(true); // Only show loader if fetching new data
        setPage(newPage);
        // prefetchPages will run via useEffect
      }
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      buttons.push(
        <button key="first" onClick={() => goToPage(1)} className={clsx("px-2 py-1 rounded text-xs font-medium", { "bg-gray-700 text-gray-300 hover:bg-gray-600": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300": !isDark, })}>1</button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="px-1 text-gray-500">...</span>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} onClick={() => goToPage(i)} className={clsx("px-2 py-1 rounded text-xs font-medium", {
          "bg-blue-600 text-white": i === page && isDark,
          "bg-blue-500 text-white": i === page && !isDark,
          "bg-gray-700 text-gray-300 hover:bg-gray-600": i !== page && isDark,
          "bg-gray-200 text-gray-700 hover:bg-gray-300": i !== page && !isDark,
        })}>{i}</button>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="px-1 text-gray-500">...</span>);
      }
      buttons.push(
        <button key="last" onClick={() => goToPage(totalPages)} className={clsx("px-2 py-1 rounded text-xs font-medium", { "bg-gray-700 text-gray-300 hover:bg-gray-600": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300": !isDark, })}>{totalPages}</button>
      );
    }
    return buttons;
  };

  return (
    <>
      {loading ? (
        <CallTableSkeleton title="Call Analytics" />
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : (
      <motion.div
        className={clsx(
            "backdrop-blur-md shadow-lg rounded-xl p-2 border w-full mx-auto my-6",
          {
            "bg-gray-800 bg-opacity-50 border-gray-700 text-gray-100": isDark,
            "bg-white border-gray-200 text-gray-900": !isDark,
          }
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
          style={{ minHeight: '80vh' }}
      >
          {/* Top Controls Row */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
            <div className="w-full sm:w-auto flex-1 flex justify-start">
          <DateRangeSelector onDateRangeChange={handleDateRangeChange} />
        </div>
            <div className="w-full sm:w-auto flex-1 flex justify-end items-center gap-2">
              <button onClick={() => goToPage(page - 1)} disabled={page === 1} className={clsx("p-2 rounded-md", { "bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed": !isDark, })}><ChevronLeft className="w-4 h-4" /></button>
              <div className="flex items-center gap-1">{renderPaginationButtons()}</div>
              <button onClick={() => goToPage(page + 1)} disabled={page === totalPages || loading} className={clsx("p-2 rounded-md", { "bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed": !isDark, })}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

          {/* Table */}
          <div className="rounded-lg">
            <table className="w-full border-collapse table-fixed" style={{ width: '100%', tableLayout: 'fixed' }}>
              <thead className={clsx("sticky top-0 z-10", { "bg-gray-900": isDark, "bg-gray-100": !isDark, })}>
            <tr>
                {columns.map((column) => {
                  let width;
                  let thPadding = '';
                  switch (column.id) {
                    case 'Date': width = '90px'; thPadding = 'px-1 py-1'; break;
                    case 'Time': width = '60px'; thPadding = 'px-1 py-1'; break;
                    case 'Caller': width = '110px'; thPadding = 'px-1 py-1'; break;
                    case 'CustomerSatisfaction': width = '90px'; thPadding = 'px-1 py-1'; break;
                    case 'CallPurpose': width = '120px'; thPadding = 'px-1 py-1'; break;
                    case 'PreferredLanguage': width = '100px'; thPadding = 'px-1 py-1'; break;
                    case 'Sentiment': width = '80px'; thPadding = 'px-1 py-1'; break;
                    case 'Summary': width = '150px'; thPadding = 'px-1 py-1'; break;
                    case 'Transcript': width = '150px'; thPadding = 'px-1 py-1'; break;
                    case 'Recording': width = '70px'; thPadding = 'px-1 py-1'; break;
                    default: width = 'auto'; thPadding = 'px-2 py-1';
                  }
                  return (
                    <th
                      key={column.id}
                      className={clsx(thPadding, "text-xs align-middle text-left font-semibold tracking-wide", { "text-gray-200": isDark, "text-gray-700": !isDark, })}
                      style={
                        ["CallPurpose", "Transcript", "Summary"].includes(column.id)
                          ? { width }
                          : { width }
                      }
                    >
                      {column.label}
                    </th>
                  );
                })}
            </tr>
          </thead>
              <tbody className={clsx("divide-y", { "divide-gray-700": isDark, "divide-gray-200": !isDark, })}>
            <AnimatePresence>
                  {analyticsData.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <td colSpan={columns.length} className="px-6 py-12 text-center">
                        <span className={clsx("text-sm", { "text-gray-400": !isDark, "text-gray-500": isDark, })}>No data available.</span>
                  </td>
                </motion.tr>
              ) : (
                analyticsData.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={clsx("group transition-colors duration-150", {
                          "bg-gray-800": isDark && index % 2 === 1,
                          "bg-gray-900": isDark && index % 2 === 0,
                          "bg-white": !isDark && index % 2 === 0,
                          "bg-gray-50": !isDark && index % 2 === 1,
                          "hover:bg-blue-900/30": isDark,
                          "hover:bg-blue-100": !isDark,
                    })}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={clsx(
                          (["CallPurpose", "Transcript", "Summary"].includes(column.id) ? "px-2 py-1" : "px-1 py-1"),
                          "text-xs align-middle",
                          { "text-gray-300": isDark, "text-gray-700": !isDark, }
                        )}
                        style={
                          ["CallPurpose", "Transcript", "Summary"].includes(column.id)
                            ? { minWidth: '180px', maxWidth: '350px', width: 'auto', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis' }
                            : { width: column.id !== 'Date' && column.id !== 'SpokenLanguage' ? undefined : undefined, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis' }
                        }
                      >
                        {column.id === 'Date' ? (
                          row[column.id] || 'N/A'
                        ) : column.id === 'Time' ? (
                          row[column.id] || 'N/A'
                        ) : column.id === 'Caller' ? (
                          row[column.id] || 'N/A'
                        ) : column.id === 'CustomerSatisfaction' ? (
                          row[column.id] ? (
                            <span className={clsx("px-2 py-1 rounded-md text-xs font-medium", {
                              "bg-green-600 text-white": row[column.id] >= 4,
                              "bg-yellow-600 text-white": row[column.id] >= 3 && row[column.id] < 4,
                              "bg-red-600 text-white": row[column.id] < 3,
                            })}>
                              {row[column.id]}
                            </span>
                          ) : 'N/A'
                        ) : column.id === 'CallPurpose' || column.id === 'Transcript' || column.id === 'Summary' ? (
                          row[column.id] && row[column.id] !== 'No Transcript' && row[column.id] !== 'No Summary' && row[column.id] !== 'Unknown' ? (
                            <div
                              onClick={() => handleExpand(index, column.id, row[column.id])}
                              className={clsx(
                                "transition-all duration-150 cursor-pointer overflow-hidden rounded-md select-text flex items-center",
                                isDark ? "" : ""
                              )}
                              style={{
                                background: 'none',
                                border: 'none',
                                Width: '180px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = isDark ? '#1e3a8a22' : '#eff6ff';
                                e.currentTarget.style.border = isDark ? '1px solid #1e40af' : '1px solid #60a5fa';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.border = 'none';
                              }}
                              title={row[column.id]}
                            >
                              <span
                                className="flex-1"
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'normal',
                                  lineHeight: 1.4,
                                  minWidth: 0,
                                }}
                              >
                                {row[column.id]}
                              </span>
                              <span style={{ marginLeft: 4, flexShrink: 0, opacity: 1, color: isDark ? '#93c5fd' : '#60a5fa', background: isDark ? '#1e40af11' : '#e0f2fe', borderRadius: '50%', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ChevronDown size={16} />
                              </span>
                            </div>
                          ) : 'N/A'
                        ) : column.id === 'PreferredLanguage' ? (
                          row[column.id] || 'N/A'
                        ) : column.id === 'Sentiment' ? (
                          row[column.id] ? (
                            <span className={clsx("px-2 py-1 rounded-md text-xs font-medium", {
                              "bg-green-600 text-white": row[column.id] === 'Positive',
                              "bg-yellow-600 text-white": row[column.id] === 'Neutral',
                              "bg-red-600 text-white": row[column.id] === 'Negative',
                            })}>
                              {row[column.id]}
                            </span>
                          ) : 'N/A'
                        ) : column.id === 'Recording' ? (
                          row[column.id] ? (
                            <button
                              onClick={() => handlePlayPause(row[column.id])}
                              className={clsx("flex items-center justify-center p-2 rounded-full", {
                                "bg-blue-500 text-white": !isDark,
                                "bg-blue-700 text-white": isDark,
                              })}
                            >
                              {currentlyPlaying === row[column.id] ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                          ) : (
                            'N/A'
                          )
                        ) : (
                          row[column.id] || 'N/A'
                        )}
                      </td>
                    ))}
                  </motion.tr>
                ))
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
      </motion.div>
      )}
      <Dialog open={expandedCell.row !== null} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <div className="p-6">
          <div className="mb-4 font-semibold text-lg">{expandedCell.column}</div>
          <div className="whitespace-pre-wrap break-words text-sm" style={{ maxHeight: 400, overflowY: 'auto' }}>{modalContent}</div>
          <div className="mt-4 flex justify-end">
            <button onClick={handleCloseModal} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Close</button>
          </div>
        </div>
      </Dialog>
      <audio id="audio-player" style={{ display: 'none' }} />
    </>
  );
};

export default CallAnalytics;
